import {
  Box,
  Chip,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import RestoreIcon from "@mui/icons-material/Restore";
import HistoryIcon from "@mui/icons-material/History";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import HandymanOutlinedIcon from "@mui/icons-material/HandymanOutlined";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import { GfFlyerReviewChangeRequest } from "@swagger/typescript-fetch-goflyer";
import { messages } from "../../../../../locales/messages";
import { ReviewChangeRequest } from "../../types";
import { ChangeRow } from "../../styled";
import { reviewTokens } from "../../theme";
import { relativeFromNow, absoluteTime } from "../CommentsPanel/time";
import { isRestorable } from "./restoreEligibility";
import { resolveRestoreTooltip } from "./restoreReasonMessages";
import {
  isImageUrlValue,
  isManualSignalField,
  manualSignalFieldLabel,
  parseDeleteItemReason,
  parseSwapPartnerId,
} from "./manualSignalCr";
import { CrSubmitterResolver } from "./useCrSubmitter";
import { SubmitterPill } from "./SubmitterPill";
import { SwapPartnerPreview } from "./SwapPartnerPreview";
import { ItemSummary } from "./useFlyerItemSummaries";
import { ValuePane, formatValue } from "./shared/ValuePane";
import { CrStatusChip } from "./shared/CrStatusChip";
import { RevertChips } from "./shared/RevertChips";
import { LazyThumb } from "../LazyThumb";

const St = GfFlyerReviewChangeRequest.StatusEnum;

interface Props {
  cr: ReviewChangeRequest;
  busy: boolean;
  flyerPublicationStatus?: string;
  itemSummaries: Map<string, ItemSummary | "loading">;
  reqOptions: { headers: { Authorization: string } };
  resolveSubmitter: CrSubmitterResolver;
  onAccept: (cr: ReviewChangeRequest) => void;
  onReject: (cr: ReviewChangeRequest) => void;
  onRestore: (cr: ReviewChangeRequest) => void;
  onHistory: (cr: ReviewChangeRequest) => void;
}

const ManualSignalCrBody = memo(function ManualSignalCrBody({
  cr,
  itemSummaries,
  reqOptions,
}: {
  cr: ReviewChangeRequest;
  itemSummaries: Map<string, ItemSummary | "loading">;
  reqOptions: { headers: { Authorization: string } };
}) {
  const { t } = useTranslation();
  const fieldKey = String(cr.fieldKey);

  if (fieldKey === "image" && isImageUrlValue(cr.newValue)) {
    return (
      <Stack spacing={1}>
        <Typography variant="body2" color="text.secondary">
          {t(messages.Review_Manual_Cr_Image_Hint())}
        </Typography>
        <Box
          sx={{
            width: 96,
            height: 96,
            borderRadius: `${reviewTokens.radiusSm}px`,
            border: `1px solid ${reviewTokens.border}`,
            overflow: "hidden",
          }}
        >
          <LazyThumb src={cr.newValue} size={96} fit="cover" />
        </Box>
        <Link
          href={cr.newValue}
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
          sx={{ wordBreak: "break-all" }}
        >
          {cr.newValue}
        </Link>
      </Stack>
    );
  }

  if (fieldKey === "deleteItem") {
    const reason = parseDeleteItemReason(cr.newValue);
    return (
      <Typography variant="body2" color="text.primary">
        {reason
          ? t(messages.Review_Manual_Cr_Delete_Reason(), { reason })
          : t(messages.Review_Manual_Cr_Field_Delete_Item())}
      </Typography>
    );
  }

  if (fieldKey === "swapPosition") {
    const partnerId = parseSwapPartnerId(cr.newValue);
    if (partnerId) {
      return (
        <SwapPartnerPreview
          partnerId={partnerId}
          itemSummaries={itemSummaries}
          reqOptions={reqOptions}
        />
      );
    }
    return (
      <Typography variant="body2" color="text.primary">
        {formatValue(cr.newValue)}
      </Typography>
    );
  }

  return (
    <Typography variant="body2" sx={{ fontFamily: reviewTokens.fontMono, fontSize: 12 }}>
      {formatValue(cr.newValue)}
    </Typography>
  );
});

export const ChangeRequestRow = memo(function ChangeRequestRow({
  cr,
  busy,
  flyerPublicationStatus,
  itemSummaries,
  reqOptions,
  resolveSubmitter,
  onAccept,
  onReject,
  onRestore,
  onHistory,
}: Props) {
  const { t } = useTranslation();
  const ts = cr.submittedAt || cr.createdAt;
  const eligibility = isRestorable(cr, flyerPublicationStatus);
  const restoreTooltipText = resolveRestoreTooltip(eligibility, t);
  const isRevertCr = !!cr.metadata?.restoreOf;
  const wasRevertedLater = !!cr.metadata?.restoredByCrId;
  const manualSignal = isManualSignalField(cr.fieldKey);
  const fieldLabel = cr.fieldKey
    ? manualSignal
      ? manualSignalFieldLabel(cr.fieldKey, t)
      : String(cr.fieldKey)
    : "—";
  const acceptTooltip = manualSignal
    ? t(messages.Review_Manual_Cr_Accept_Help())
    : t(messages.Accept());

  return (
    <ChangeRow data-testid={`cr-row-${cr.id}`}>
      <Stack
        direction="column"
        spacing={1.25}
        sx={{ width: "100%", minWidth: 0 }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          sx={{ rowGap: 0.5 }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            sx={{ minWidth: 0, rowGap: 0.5 }}
          >
            <Typography
              variant="body2"
              sx={{
                fontFamily: manualSignal ? undefined : reviewTokens.fontMono,
                fontWeight: 600,
                fontSize: 13,
                color: "text.primary",
                px: 0.75,
                py: 0.25,
                bgcolor: manualSignal ? reviewTokens.brandSoft : reviewTokens.slate100,
                borderRadius: `${reviewTokens.radiusSm}px`,
                border: manualSignal
                  ? `1px solid ${reviewTokens.brand}`
                  : undefined,
              }}
            >
              {fieldLabel}
            </Typography>
            {manualSignal ? (
              <Chip
                size="small"
                icon={<HandymanOutlinedIcon sx={{ fontSize: "14px !important" }} />}
                label={t(messages.Review_Manual_Cr_Notice_Title())}
                variant="outlined"
                sx={{
                  height: 22,
                  fontWeight: 600,
                  color: reviewTokens.brandHover,
                  borderColor: reviewTokens.brand,
                  "& .MuiChip-icon": { color: reviewTokens.brand },
                }}
              />
            ) : null}
            <CrStatusChip status={cr.status} tone="chip" />
            <RevertChips
              isRevertCr={isRevertCr}
              wasRevertedLater={wasRevertedLater}
              withTooltip
              withIcon
            />
            {ts ? (
              <Tooltip title={absoluteTime(ts)}>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary" }}
                >
                  {relativeFromNow(ts)}
                </Typography>
              </Tooltip>
            ) : null}
            <SubmitterPill cr={cr} resolve={resolveSubmitter} />
          </Stack>
          <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
            {cr.status === St.PENDING && (
              <>
                <Tooltip title={acceptTooltip}>
                  <span>
                    <IconButton
                      size="small"
                      disabled={busy}
                      onClick={() => onAccept(cr)}
                      data-testid={`cr-accept-${cr.id}`}
                      sx={{
                        color: "success.main",
                        "&:hover": {
                          bgcolor: "rgba(22, 163, 74, 0.08)",
                        },
                      }}
                    >
                      <CheckIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={t(messages.Reject())}>
                  <span>
                    <IconButton
                      size="small"
                      disabled={busy}
                      onClick={() => onReject(cr)}
                      data-testid={`cr-reject-${cr.id}`}
                      sx={{
                        color: "error.main",
                        "&:hover": {
                          bgcolor: "rgba(220, 38, 38, 0.08)",
                        },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
            {cr.status === St.ACCEPTED && (
              <Tooltip title={restoreTooltipText}>
                <span>
                  <IconButton
                    size="small"
                    disabled={busy || !eligibility.ok}
                    onClick={() => onRestore(cr)}
                    data-testid={`cr-restore-${cr.id}`}
                  >
                    <RestoreIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title={t(messages.View_History())}>
              <span>
                <IconButton
                  size="small"
                  disabled={!cr.flyerItemId || !cr.fieldKey}
                  onClick={() => onHistory(cr)}
                  data-testid={`cr-history-${cr.id}`}
                  sx={{ color: "text.secondary" }}
                >
                  <HistoryIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        {manualSignal ? (
          <Stack spacing={0.75}>
            {cr.status === St.PENDING ? (
              <Box
                sx={{
                  display: "flex",
                  gap: 0.75,
                  alignItems: "flex-start",
                  px: 1,
                  py: 0.75,
                  borderRadius: `${reviewTokens.radiusSm}px`,
                  bgcolor: reviewTokens.brandSoft,
                  border: `1px solid ${reviewTokens.brand}`,
                }}
              >
                <HandymanOutlinedIcon
                  sx={{ fontSize: 16, color: reviewTokens.brand, mt: 0.125, flexShrink: 0 }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      fontWeight: 700,
                      fontSize: "0.6875rem",
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                      color: reviewTokens.brandHover,
                      lineHeight: 1.3,
                    }}
                  >
                    {t(messages.Review_Manual_Cr_Notice_Title())}
                  </Typography>
                  <Typography
                    variant="caption"
                    component="p"
                    sx={{
                      m: 0,
                      mt: 0.25,
                      fontSize: "0.75rem",
                      lineHeight: 1.4,
                      color: reviewTokens.textPrimary,
                    }}
                  >
                    {t(messages.Review_Manual_Cr_Notice_Body())}{" "}
                    {t(messages.Review_Manual_Cr_Step_Accept())}
                  </Typography>
                </Box>
              </Box>
            ) : null}
            <Box
              sx={{
                p: 1,
                borderRadius: `${reviewTokens.radiusSm}px`,
                bgcolor: reviewTokens.slate50,
                border: `1px solid ${reviewTokens.border}`,
                borderLeft: `2px solid ${reviewTokens.brand}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "text.secondary",
                  display: "block",
                  mb: 0.75,
                }}
              >
                {t(messages.Review_Manual_Cr_Requested_Change())}
              </Typography>
              <ManualSignalCrBody
                cr={cr}
                itemSummaries={itemSummaries}
                reqOptions={reqOptions}
              />
            </Box>
          </Stack>
        ) : (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems="stretch"
          >
            <ValuePane
              fieldKey={String(cr.fieldKey)}
              value={cr.oldValue}
              variant="before"
            />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: { sm: 0.25 },
                py: { xs: 0.25, sm: 0 },
              }}
            >
              <ArrowForwardIcon
                sx={{
                  fontSize: 14,
                  color: "text.disabled",
                  transform: { xs: "rotate(90deg)", sm: "none" },
                }}
              />
            </Box>
            <ValuePane
              fieldKey={String(cr.fieldKey)}
              value={cr.newValue}
              variant="after"
            />
          </Stack>
        )}
      </Stack>
    </ChangeRow>
  );
});
